import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { space } from 'folio-common-components';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { CompanyInput } from '../../components/CompanyInput';
import { OrgItem } from '../../components/CompanyInput/ControlledCompanyInput';
import { PersonInput } from '../../components/PersonInput';
import { BoxHeading } from '../../components/box';
import { Button, TextButton } from '../../components/buttons';
import { useStoreState } from '../../state';
import type { CompanySearchItem } from '../../state/companySearch';
import type { Person } from '../../state/editor';
import { getPersonByPnum } from '../../state/editor/selectors';

export interface Props {
  onCancel: () => void;
  onSave: (data: {
    org: { orgId: string; orgName: string };
    signer: Person;
  }) => void;
}

const Box = styled.div`
  border-radius: var(--border-radius);
  border: 1px solid ${colors.grayMedium};
  ${space([16], 'padding', 'margin-bottom', 'gap')};
`;

interface FormState {
  company: CompanySearchItem | undefined;
  signingPerson: Person;
  hasClickedSave: boolean;
}

export const OrgTransferOrgDetailsForm: React.FC<Props> = props => {
  const { onCancel, onSave: onSaveProp } = props;
  const rootState = useStoreState();
  const [state, setState] = React.useState<FormState>({
    company: undefined,
    hasClickedSave: false,
    signingPerson: {
      address: '',
      email: '',
      id: '',
      name: '',
      phone: '',
      pNum: '',
      postalCode: '',
      cadasterAddress: '',
    },
  });

  const onSave = React.useCallback(() => {
    setState(prev => ({ ...prev, hasClickedSave: true }));

    if (state.company?.orgId && state.company?.name) {
      const existingPerson = getPersonByPnum(
        rootState,
        state.signingPerson.pNum,
      );

      onSaveProp({
        org: { orgName: state.company.name, orgId: state.company.orgId },
        signer: { ...state.signingPerson, id: existingPerson?.id ?? '' },
      });
    }
  }, [
    onSaveProp,
    state.company?.name,
    state.company?.orgId,
    state.signingPerson,
    rootState,
  ]);

  return (
    <Box css={space([16], 'margin-top')}>
      <BoxHeading>Bedriften som skal videreføres</BoxHeading>
      <CompanyInput
        renderSuggestion={item => <OrgItem item={item} />}
        onSelect={e => {
          setState(prev => ({ ...prev, company: e }));
        }}
        selected={state.company}
      />
      {state.company && (
        <PersonInput
          inputs={['autoName', 'pNum', 'email', 'phone']}
          person={state.signingPerson}
          labelOverrides={{ name: 'Navn på den som skal signere' }}
          disableAutocomplete={true}
          onChange={person => {
            setState(prev => ({ ...prev, signingPerson: person }));
          }}
          validate={state.hasClickedSave}
          editType="new"
        />
      )}
      <div
        css={css`
          margin-top: 32px;
        `}
      >
        <Button
          level="primary"
          onClick={onSave}
          disabled={state.company?.orgId == null}
          css={space([24], 'margin-right')}
        >
          Lagre
        </Button>{' '}
        <TextButton onClick={onCancel}>Avbryt</TextButton>
      </div>
    </Box>
  );
};
